import { VerticalPaginationWrapper } from '@/components/PostWrapper';
import { Divider } from '@/components/Shared';
import { Button, Empty, Input, Radio, Select, Space } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { SearchIcon } from '@/assets/icons';
import { useRouter } from 'next/router';
import {
  GetBannerDocument,
  GetSeoSettingsDocument,
  GetSourceDocument,
  Image_Subtypes_Enum,
  Order_By,
  SearchPostsDocument,
  useGetBannerQuery,
  useGetSourceQuery,
  useSearchPostsQuery,
} from '@/generated/graphql';
import { LIMIT_POSTS } from '@/constants/post';
import { GetServerSideProps } from 'next';
import { addApolloState, initializeApollo } from '@/lib/apolloClient';
import format from '@/utils/format';
import { SEOConfig } from 'next-seo.config';
import Banner from '@/components/Shared/Banner';
import { GetBannerSearchParams } from '@/constants/defaultParams';

const TIME_PUBLISH = ['Tất cả', 'Hôm nay', '7 ngày trước', '30 ngày trước'];

const Search = () => {
  const router = useRouter();
  const { query } = router;
  // const localeRef = locale === 'vi' ? 'vi_VN' : 'en_US';
  const keyword = (query?.keyword ?? '') as string;
  const page = parseInt((query?.page ?? 1) as string);
  const order = (query?.order ?? '') as string;
  const { data: dataSource } = useGetSourceQuery();
  const [valueSource, setValueSource] = useState(0);
  const [valueTime, setValueTime] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState(keyword);

  useEffect(() => setSearchKeyword(keyword), [keyword]);

  const [params, setParams] = useState({
    keyword,
    page,
    order,
  });

  useEffect(
    () => setParams(prev => ({ ...prev, keyword, page, order })),
    [keyword, page, order],
  );

  const queryVariables: any = {
    args: {
      search: params.keyword,
      lang: 'vi_VN',
    },
    limit: LIMIT_POSTS,
    offset: (params.page - 1) * LIMIT_POSTS,
    orderBy: [
      {
        published_at:
          params.order === 'newest' || !order ? Order_By.Desc : null,
      },
      {
        total_views: params.order === 'hotest' ? Order_By.Desc : null,
      },
    ],
  };

  if (valueSource !== 0) {
    queryVariables.where = {
      posts_sources: {
        source_id: {
          _eq: valueSource,
        },
      },
    };
  }

  const { data } = useSearchPostsQuery({
    variables: queryVariables,
  });

  const postData = format.formatDataPost(data?.posts);

  const totalCount = useMemo(
    () => data?.searchPostsAggregate?.aggregate?.count,
    [data?.searchPostsAggregate],
  );

  const handleChangeOrder = (value: string) => {
    router.push({
      query: {
        ...query,
        order: value,
      },
    });
  };

  const handleChangeKeyword = () => {
    if (searchKeyword) {
      router.push({
        query: {
          keyword: searchKeyword,
        },
      });
    }
  };

  const { data: dataBanner } = useGetBannerQuery({
    variables: GetBannerSearchParams,
  });

  return (
    <div className="container mt-5 flex flex-col gap-8">
      <SEOConfig title={'Tìm kiếm | Tin Hay Nhà Đất'} />
      <div className="w-full h-[158px]">
        <Banner
          data={dataBanner?.banners.filter(
            item => item.position === Image_Subtypes_Enum.SearchTop,
          )}
        />
      </div>

      <div className="flex gap-5">
        <div className="hidden lg:flex flex-col h-fit gap-8 bg-[#d9d9d9] rounded-[10px] p-8 filter-search">
          <div className="flex flex-col gap-3 text-black">
            <h4 className="uppercase text-[19px] font-bold">Nguồn bài viết</h4>
            <Radio.Group
              value={valueSource}
              onChange={e => setValueSource(e.target.value)}
            >
              <Space direction="vertical">
                <Radio value={0} key={-1} className="!text-[19px] !font-bold">
                  Tất cả
                </Radio>
                {dataSource?.sources?.map((item, index) => {
                  return (
                    <Radio
                      value={item?.id}
                      key={index}
                      className="!text-[19px] !font-bold"
                    >
                      {item?.name}
                    </Radio>
                  );
                })}
              </Space>
            </Radio.Group>
          </div>
          <div className="flex flex-col gap-3 text-black">
            <h4 className="uppercase text-[19px] font-bold">Thời gian đăng</h4>
            <Radio.Group
              value={valueTime}
              onChange={e => setValueTime(e.target.value)}
            >
              <Space direction="vertical">
                {TIME_PUBLISH.map((item, index) => {
                  return (
                    <Radio
                      value={index}
                      key={index}
                      className="!text-[19px] !font-bold"
                    >
                      {item}
                    </Radio>
                  );
                })}
              </Space>
            </Radio.Group>
          </div>
        </div>

        <div className="flex-1">
          <div className="flex flex-col lg:flex-row lg:justify-between">
            <p className="text-[17px] font-bold w-fit ">
              Kết quả tìm kiếm cho
              <span className="pl-1 font-normal">
                {keyword ? `"${keyword}"` : `toàn bộ bài viết`}
              </span>
            </p>
            <p className="text-[17px] font-normal">
              Có <span className="text-red-500">{totalCount}</span> kết quả tìm
              kiếm
            </p>
          </div>

          <Divider />

          <div className="flex flex-col lg:flex-row gap-4 lg:gap-8 lg:h-[50px] items-center py-4 lg:py-[5px] bg-[#d9d9d9] rounded-[10px] px-6">
            <p className="text-[17px] font-normal">Nội dung tìm kiếm</p>
            <Select
              value={params.order || 'newest'}
              className="w-full lg:w-fit"
              style={{ height: '100%', minWidth: '160px' }}
              onChange={value => handleChangeOrder(value)}
              options={[
                { value: 'newest', label: 'Mới nhất' },
                { value: 'hotest', label: 'Nổi nhất' },
              ]}
              optionRender={option => {
                return (
                  <div className="font-bold text-base text-[#454444] py-1 px-4">
                    {option.label}
                  </div>
                );
              }}
            />

            <div className="flex flex-col lg:flex-row gap-3 flex-1 h-full w-full lg:w-fit">
              <Input.Search
                placeholder="Tìm kiếm ..."
                value={searchKeyword}
                onSearch={handleChangeKeyword}
                onChange={e => setSearchKeyword(e.target.value)}
                className="lg:w-[200px] w-full"
                style={{ flexGrow: '1', height: '100%' }}
                allowClear
                enterButton={<SearchIcon />}
              />

              <Button
                type="primary"
                onClick={handleChangeKeyword}
                style={{
                  height: '100%',
                  fontSize: '17px',
                  fontWeight: 'bold',
                  color: 'white',
                  minWidth: '160px',
                  background: '#006097',
                }}
              >
                Tìm kiếm
              </Button>
            </div>
          </div>

          {totalCount !== 0 ? (
            <VerticalPaginationWrapper
              data={postData}
              totalCount={totalCount}
            />
          ) : (
            <Empty
              className="mt-10"
              description={<>Không có kết quả tìm kiếm phù hợp!</>}
            />
          )}
        </div>
      </div>

      <div className="flex gap-5">
        <div className="w-full h-[36vw] lg:h-[465px]">
          <Banner
            data={dataBanner?.banners.filter(
              item => item.position === Image_Subtypes_Enum.SearchBottom_1,
            )}
          />
        </div>
        <div className="w-full h-[36vw] lg:h-[465px]">
          <Banner
            data={dataBanner?.banners.filter(
              item => item.position === Image_Subtypes_Enum.SearchBottom_2,
            )}
          />
        </div>
        <div className="w-full h-[36vw] lg:h-[465px]">
          <Banner
            data={dataBanner?.banners.filter(
              item => item.position === Image_Subtypes_Enum.SearchBottom_3,
            )}
          />
        </div>
      </div>
    </div>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  const { query, locale } = context;
  const apolloClient = initializeApollo({ locale });
  const { keyword, page = 1, order } = query;
  const localeRef = locale === 'vi' ? 'vi_VN' : 'en_US';
  await apolloClient.query({
    query: GetSeoSettingsDocument,
  });
  await apolloClient.query({
    query: GetSourceDocument,
  });
  await apolloClient.query({
    query: GetBannerDocument,
    variables: GetBannerSearchParams,
  });
  await apolloClient.query({
    query: SearchPostsDocument,
    variables: {
      args: {
        search: keyword,
        lang: localeRef,
      },
      limit: LIMIT_POSTS,
      offset: (parseInt(page as string) - 1) * LIMIT_POSTS,
      orderBy: [
        {
          published_at: order === 'newest' || !order ? Order_By.Desc : null,
        },
        {
          total_views: order === 'hotest' ? Order_By.Desc : null,
        },
      ],
    },
  });

  return addApolloState(apolloClient, {
    props: {},
  });
};

export default Search;
